import { displayUserName } from "@modules/user/deletedUser";
import React from "react";
import { format } from "date-fns";
import { RotateCcw } from "lucide-react";

import { PageTemplate } from "@components/pageTemplate/PageTemplate";
import { useAuditLogActionTypesQuery, useAuditLogsQuery } from "generated/hooks";
import { Routes } from "~/routes";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "~/components/ui/pagination";

const ALL_ACTION_TYPES = "ALL_ACTION_TYPES";

const toIsoDateTime = (value: string) =>
  value ? new Date(value).toISOString() : null;

const getUserDisplayName = (
  user?: { fullName?: string | null; email?: string | null } | null
) => {
  return displayUserName(user, "System / unknown");
};

const formatAuditTimestamp = (value: string) =>
  format(new Date(value), "yyyy-MM-dd HH:mm:ss");

const AuditLogsPage = () => {
  const [page, setPage] = React.useState(0);
  const [pageCursors, setPageCursors] = React.useState<
    Map<number, string | null>
  >(new Map([[0, null]]));
  const [selectedActionType, setSelectedActionType] =
    React.useState(ALL_ACTION_TYPES);
  const [occurredFrom, setOccurredFrom] = React.useState("");
  const [occurredTo, setOccurredTo] = React.useState("");
  const [userName, setUserName] = React.useState("");
  const [filtersCollapsed, setFiltersCollapsed] = React.useState(true);

  const currentPageCursor = pageCursors.get(page) ?? null;
  const actionTypeVariable =
    selectedActionType === ALL_ACTION_TYPES ? null : selectedActionType;
  const occurredFromVariable = React.useMemo(
    () => toIsoDateTime(occurredFrom),
    [occurredFrom]
  );
  const occurredToVariable = React.useMemo(
    () => toIsoDateTime(occurredTo),
    [occurredTo]
  );
  const userNameVariable = userName.trim() || null;
  const activeFiltersCount =
    Number(selectedActionType !== ALL_ACTION_TYPES) +
    Number(Boolean(occurredFrom)) +
    Number(Boolean(occurredTo)) +
    Number(Boolean(userNameVariable));

  const resetPagination = React.useCallback(() => {
    setPage(0);
    setPageCursors(new Map([[0, null]]));
  }, []);

  const { data: actionTypesData, loading: isActionTypesLoading } =
    useAuditLogActionTypesQuery();

  const {
    data,
    error,
    loading: isAuditLogsLoading,
  } = useAuditLogsQuery({
    variables: {
      after: currentPageCursor,
      actionType: actionTypeVariable,
      occurredFrom: occurredFromVariable,
      occurredTo: occurredToVariable,
      userName: userNameVariable,
    },
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const auditLogs = data?.auditLogs?.nodes?.filter(Boolean) ?? [];
  const pageInfo = data?.auditLogs?.pageInfo;
  const hasNextPage = Boolean(pageInfo?.hasNextPage);
  const hasPreviousPage = page > 0;

  React.useEffect(() => {
    if (pageInfo?.hasNextPage && pageInfo.endCursor) {
      setPageCursors((previous) => {
        if (previous.get(page + 1) === pageInfo.endCursor) {
          return previous;
        }

        const next = new Map(previous);
        next.set(page + 1, pageInfo.endCursor ?? null);
        return next;
      });
    }
  }, [page, pageInfo?.endCursor, pageInfo?.hasNextPage]);

  const handleClearFilters = React.useCallback(() => {
    setSelectedActionType(ALL_ACTION_TYPES);
    setOccurredFrom("");
    setOccurredTo("");
    setUserName("");
    resetPagination();
  }, [resetPagination]);

  const handleOccurredFromChange = React.useCallback(
    (value: string) => {
      setOccurredFrom(value);
      resetPagination();
    },
    [resetPagination]
  );

  const handleOccurredToChange = React.useCallback(
    (value: string) => {
      setOccurredTo(value);
      resetPagination();
    },
    [resetPagination]
  );

  return (
    <PageTemplate
      title="Audit Logs"
      breadcrumbs={[
        { label: "Super admin", path: Routes.TrainedIndicators },
        { label: "Audit Logs", path: Routes.AuditLogs },
      ]}
      subtitle="Review administrative audit activity across the platform"
      activeItem="Super admin"
      activeSubItem="Audit Logs"
      headerRule
    >
      <Card className="overflow-hidden gap-0 rounded-lg">
        <CardHeader className="flex flex-col gap-3 pb-0 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle className="text-base font-semibold text-foreground">
            Filters
          </CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setFiltersCollapsed((previous) => !previous)}
            >
              <span>
                {filtersCollapsed ? "Show filters" : "Hide filters"}
                {activeFiltersCount > 0 ? ` (${activeFiltersCount})` : ""}
              </span>
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleClearFilters}
              disabled={activeFiltersCount === 0}
            >
              <RotateCcw className="size-4" />
              Clear
            </Button>
          </div>
        </CardHeader>
        {!filtersCollapsed && (
          <CardContent className="grid gap-4 pb-0 pt-6 md:grid-cols-2 xl:grid-cols-[minmax(180px,240px)_minmax(180px,1fr)_minmax(180px,1fr)_minmax(180px,1fr)]">
            <div className="flex flex-col gap-2">
              <Label htmlFor="audit-action-type">Action type</Label>
              <Select
                value={selectedActionType}
                onValueChange={(value) => {
                  setSelectedActionType(value);
                  resetPagination();
                }}
                disabled={isActionTypesLoading}
              >
                <SelectTrigger id="audit-action-type" className="w-full">
                  <SelectValue placeholder="All action types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_ACTION_TYPES}>
                    All action types
                  </SelectItem>
                  {(actionTypesData?.auditLogActionTypes ?? []).map(
                    (actionType) => (
                      <SelectItem key={actionType} value={actionType}>
                        {actionType}
                      </SelectItem>
                    )
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="audit-occurred-from">From</Label>
              <Input
                id="audit-occurred-from"
                type="datetime-local"
                value={occurredFrom}
                onChange={(event) =>
                  handleOccurredFromChange(event.target.value)
                }
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="audit-occurred-to">To</Label>
              <Input
                id="audit-occurred-to"
                type="datetime-local"
                value={occurredTo}
                onChange={(event) => handleOccurredToChange(event.target.value)}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="audit-user-name">User name</Label>
              <Input
                id="audit-user-name"
                value={userName}
                onChange={(event) => {
                  setUserName(event.target.value);
                  resetPagination();
                }}
                placeholder="Search name or email"
              />
            </div>
          </CardContent>
        )}
      </Card>

      <Card className="rounded-lg py-0">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Action type</TableHead>
                <TableHead>Target entity type</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead>Timestamp</TableHead>
                <TableHead>User</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isAuditLogsLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="h-24 text-center text-sm text-muted-foreground"
                  >
                    Loading audit logs...
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="h-24 text-center text-sm text-destructive"
                  >
                    Failed to load audit logs.
                  </TableCell>
                </TableRow>
              ) : auditLogs.length > 0 ? (
                auditLogs.map((auditLog) => (
                  <TableRow key={auditLog.id}>
                    <TableCell className="font-medium">
                      {auditLog.actionType}
                    </TableCell>
                    <TableCell>
                      {auditLog.targetEntityType ?? "Not specified"}
                    </TableCell>
                    <TableCell className="max-w-[280px] whitespace-normal text-muted-foreground">
                      {auditLog.reason ?? "Not specified"}
                    </TableCell>
                    <TableCell className="max-w-[320px] whitespace-normal text-muted-foreground">
                      {auditLog.notes ?? "Not specified"}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatAuditTimestamp(auditLog.createdAt)}
                    </TableCell>
                    <TableCell>
                      {getUserDisplayName(auditLog.actingUser)}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="h-24 text-center text-sm text-muted-foreground"
                  >
                    No audit logs found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Pagination>
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious
              aria-disabled={!hasPreviousPage}
              className={
                hasPreviousPage
                  ? "cursor-pointer"
                  : "pointer-events-none opacity-50"
              }
              onClick={(event) => {
                event.preventDefault();
                if (hasPreviousPage) {
                  setPage((previous) => previous - 1);
                }
              }}
            />
          </PaginationItem>
          <PaginationItem>
            <span className="px-3 text-sm text-muted-foreground">
              Page {page + 1}
            </span>
          </PaginationItem>
          <PaginationItem>
            <PaginationNext
              aria-disabled={!hasNextPage}
              className={
                hasNextPage
                  ? "cursor-pointer"
                  : "pointer-events-none opacity-50"
              }
              onClick={(event) => {
                event.preventDefault();
                if (hasNextPage) {
                  setPage((previous) => previous + 1);
                }
              }}
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </PageTemplate>
  );
};

export default AuditLogsPage;
